import React, { useEffect } from "react";
import { useParams } from "react-router";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { Col } from "@amzn/stencil-react-components/layout";
import { Text as StencilText } from "@amzn/stencil-react-components/text";
import { IconCheck } from "../../components/icons/check";
import { H1, Text } from "../../components/typography";
import { defaultTextColor } from "../../config/palette";
import { buildUrl } from "../../config/urls";
import { Task, taskGroups, taskGroupsForDGS } from "../../config/tasks";
import { TasksGroup } from "../../components/task";
import { Stage } from "../../helpers/get-settings";
import headerImageSrc from "./header.png";
import * as adobeAnalytics from "../../utility/adobe-analytics";
import { Sda } from "../../components/sda";
import { ManageApplicationCard } from "../../components/manage-app-card";
import { isDragonstone } from "../../helpers/url-params";
import { AppointmentCard } from "../../components/appointment-card";

interface ChecklistRouteProps {
  stage?: Stage;
}

export const ChecklistRoute = ({ stage }: ChecklistRouteProps) => {
  const { applicationId, requisitionId, candidateId } = useParams<{
    applicationId: string;
    requisitionId: string;
    candidateId: string;
  }>();

  useEffect(() => {
    window.ASH_CHECKLIST_isChecklistReferral = true;
    adobeAnalytics.addPageLoadMetric(window, applicationId, "Ash thank you", {
      jobId: requisitionId,
      isDragonstone: isDragonstone(requisitionId),
    });
  }, [applicationId, requisitionId]);

  const onlyNotHiddenInProd = (task: Task) =>
    !task.hideInProd || stage !== "prod";

  const targetTaskGroup = isDragonstone(requisitionId)
    ? taskGroupsForDGS
    : taskGroups;

  const isDragonStonePage = isDragonstone(requisitionId);

  const groups = targetTaskGroup.map((taskGroup) => {
    return {
      ...taskGroup,
      tasks: taskGroup.tasks.filter(onlyNotHiddenInProd).map((task) => ({
        ...task,
        href:
          task.href &&
          buildUrl(task.href, { stage, requisitionId, applicationId }),
        titleStatusPath:
          task.titleStatusPath &&
          buildUrl(task.titleStatusPath, {
            stage,
            requisitionId,
            applicationId,
            candidateId,
          }),
        subtaskStatuses:
          task.subtasks &&
          task.subtasks.map((subTask) => {
            return {
              title: subTask.titleText,
              path: subTask.titleStatusPath
                ? buildUrl(subTask.titleStatusPath, {
                    stage,
                    requisitionId,
                    applicationId,
                    candidateId,
                  })
                : undefined,
            };
          }),
      })),
    };
  });

  return (
    <>
      <PageContainer color={defaultTextColor}>
        <Col gridGap="2rem">
          {isDragonStonePage && (
            <Col gridGap="1rem" alignItems="left">
              <AppointmentCard stage={stage} />
            </Col>
          )}

          {!isDragonStonePage && (
            <Col gridGap="1rem" alignItems="center">
              <img src={headerImageSrc} alt="Amazon jobs logo" width="100%" />
              <IconCheck />
              <StencilText fontSize="T400">
                Thank you for applying to Amazon!
              </StencilText>
              <Sda />
            </Col>
          )}
          {!isDragonStonePage && (
            <Col gridGap="1rem">
              <H1>Your Pre-Day 1 Checklist</H1>
              <Text>
                Review the information below to learn more about next steps you
                need to complete prior to your first day at Amazon.
              </Text>
            </Col>
          )}
          {isDragonStonePage && (
            <Col gridGap="1rem">
              <H1>Next Steps</H1>
              <Text>
                You&#39;ll need to complete these activities before your first
                day at Amazon.
              </Text>
            </Col>
          )}
          {groups.map((taskGroup) => (
            <TasksGroup
              key={taskGroup.name}
              data={taskGroup}
              applicationId={applicationId}
              candidateId={candidateId}
            />
          ))}
          {isDragonStonePage && (
            <Col gridGap="1rem" alignItems="left">
              <ManageApplicationCard stage={stage} />
            </Col>
          )}
        </Col>
      </PageContainer>
    </>
  );
};
